import { useCallback, useRef } from 'react'
import GithubSlugger from 'github-slugger'
import ReactMarkdown from 'react-markdown'
import remarkGfm from 'remark-gfm'
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter'
import { oneDark } from 'react-syntax-highlighter/dist/esm/styles/prism'
import { MermaidDiagram } from '@/components/MermaidDiagram'
import { WorkItemCards } from '@/components/projects/chat/WorkItemCards'
import { CopyButton } from '@/components/ui/copy-button'
import { cn } from '@/lib/utils'

interface MarkdownViewerProps {
  content: string
  bodyTextClass?: string
}

const HEADING_SELECTOR = 'h1, h2, h3, h4, h5, h6'

function decodeAnchor(value: string) {
  try {
    return decodeURIComponent(value)
  } catch {
    return value
  }
}

export function MarkdownViewer({ content, bodyTextClass = 'text-muted-foreground' }: MarkdownViewerProps) {
  const containerRef = useRef<HTMLDivElement>(null)

  const scrollToSection = useCallback((href: string) => {
    const container = containerRef.current
    const anchor = decodeAnchor(href.slice(1))
    if (!container || anchor.length === 0) return

    const slugger = new GithubSlugger()
    const target = Array.from(container.querySelectorAll(HEADING_SELECTOR))
      .find(heading => slugger.slug(heading.textContent ?? '') === anchor)

    target?.scrollIntoView({ behavior: 'smooth', block: 'start' })
  }, [])

  return (
    <div ref={containerRef} className="markdown-viewer min-w-0 break-words">
      <ReactMarkdown
        remarkPlugins={[remarkGfm]}
        components={{
          h1: ({ children }) => (
            <h1 className="text-2xl font-bold text-foreground mt-6 mb-4 pb-2 border-b border-border first:mt-0">
              {children}
            </h1>
          ),
          h2: ({ children }) => (
            <h2 className="text-xl font-semibold text-foreground mt-6 mb-3 pb-1 border-b border-border/50">
              {children}
            </h2>
          ),
          h3: ({ children }) => (
            <h3 className="text-lg font-semibold text-foreground mt-5 mb-2">
              {children}
            </h3>
          ),
          h4: ({ children }) => (
            <h4 className="text-base font-semibold text-foreground mt-4 mb-2">
              {children}
            </h4>
          ),
          p: ({ children }) => (
            <p className={cn('leading-relaxed mb-4', bodyTextClass)}>
              {children}
            </p>
          ),
          ul: ({ children }) => (
            <ul className={cn('list-disc list-outside ml-6 mb-4 space-y-1', bodyTextClass)}>
              {children}
            </ul>
          ),
          ol: ({ children }) => (
            <ol className={cn('list-decimal list-outside ml-6 mb-4 space-y-1', bodyTextClass)}>
              {children}
            </ol>
          ),
          li: ({ children }) => (
            <li className="leading-relaxed">
              {children}
            </li>
          ),
          a: ({ href, children }) => {
            if (href?.startsWith('#')) {
              return (
                <a
                  href={href}
                  onClick={(event) => {
                    event.preventDefault()
                    scrollToSection(href)
                  }}
                  className="text-info-foreground hover:text-info-foreground hover:underline"
                >
                  {children}
                </a>
              )
            }

            const normalizedHref = href && !/^https?:\/\//i.test(href) ? `https://${href}` : href
            return (
              <a
                href={normalizedHref}
                target="_blank"
                rel="noopener noreferrer"
                className="text-info-foreground hover:text-info-foreground hover:underline"
              >
                {children}
              </a>
            )
          },
          blockquote: ({ children }) => (
            <blockquote className="border-l-4 border-info-foreground bg-info pl-4 pr-4 py-2 my-4 text-muted-foreground italic">
              {children}
            </blockquote>
          ),
          code: ({ className, children }) => {
            const match = /language-([\w-]+)/.exec(className || '')
            const codeString = String(children).replace(/\n$/, '')
            const language = match ? match[1] : null

            if (language === 'mermaid') {
              return <MermaidDiagram chart={codeString} />
            }

            if (language === 'work-items') {
              return <WorkItemCards data={codeString} />
            }

            const isCodeBlock = match || codeString.includes('\n')

            if (isCodeBlock) {
              const displayLanguage = language ?? 'text'
              return (
                <div className="relative group my-4">
                  <div className="absolute top-0 left-0 px-3 py-1 text-xs font-medium text-gray-400 bg-gray-800 rounded-tl rounded-br z-10">
                    {displayLanguage}
                  </div>
                  <CopyButton
                    text={codeString}
                    tooltipLabel="Copy code"
                    className="absolute top-2 right-2 z-10 bg-gray-700 text-gray-300 hover:bg-gray-600 hover:text-white"
                  />
                  <SyntaxHighlighter
                    style={oneDark as Record<string, React.CSSProperties>}
                    language={displayLanguage}
                    PreTag="div"
                    className="rounded-lg !mt-0 !pt-8"
                    customStyle={{
                      margin: 0,
                      borderRadius: '0.5rem',
                      fontSize: '0.875rem',
                    }}
                  >
                    {codeString}
                  </SyntaxHighlighter>
                </div>
              )
            }

            return (
              <code className="px-1.5 py-0.5 bg-muted text-foreground text-sm rounded font-mono">
                {children}
              </code>
            )
          },
          pre: ({ children }) => <>{children}</>,
          table: ({ children }) => (
            <div className="overflow-x-auto my-4">
              <table aria-label="Content table" className="min-w-full divide-y divide-border border border-border rounded-lg">
                {children}
              </table>
            </div>
          ),
          thead: ({ children }) => (
            <thead className="bg-muted/50">
              {children}
            </thead>
          ),
          tbody: ({ children }) => (
            <tbody className="divide-y divide-border bg-background">
              {children}
            </tbody>
          ),
          tr: ({ children }) => (
            <tr className="hover:bg-muted/30 transition-colors">
              {children}
            </tr>
          ),
          th: ({ children }) => (
            <th className="px-4 py-3 text-left text-xs font-semibold text-muted-foreground uppercase tracking-wider">
              {children}
            </th>
          ),
          td: ({ children }) => (
            <td className={cn('px-4 py-3 text-sm max-w-[32rem] break-words', bodyTextClass)}>
              {children}
            </td>
          ),
          hr: () => (
            <hr className="my-6 border-t border-border" />
          ),
          strong: ({ children }) => (
            <strong className="font-semibold text-foreground">
              {children}
            </strong>
          ),
          em: ({ children }) => (
            <em className={cn('italic', bodyTextClass)}>
              {children}
            </em>
          ),
          del: ({ children }) => (
            <del className="line-through text-muted-foreground/60">
              {children}
            </del>
          ),
          img: ({ src, alt }) => (
            <img
              src={src}
              alt={alt || ''}
              className="max-w-full h-auto rounded-lg shadow-sm my-4"
            />
          ),
        }}
      >
        {content}
      </ReactMarkdown>
    </div>
  )
}
